import { useState, useEffect, useRef } from "react";
import Papa from "papaparse";
import { useLocation } from "wouter";
import { useProfile } from "@/hooks/use-profile";
import { useRolePerspective } from "@/hooks/use-role-perspective";
import { supabase } from "@/lib/supabase";
import type {
  Topic,
  Question,
  QuestionMode,
  DifficultyLevel,
  TopicCategoryOption,
  YearLevelSummary,
} from "@shared/schema";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useSubject } from "@/contexts/SubjectContext";
import { filterTopicsBySubject } from "@/lib/utils";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ShieldCheck,
  LogOut,
  Plus,
  Edit,
  Copy,
  Trash2,
  X,
  BookOpen,
  Upload,
  Save,
  Eye,
  Download,
  GitMerge,
  Loader2,
  ArrowDown,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MathDisplay from "@/components/MathDisplay";
import MathText from "@/components/MathText";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";

import { getRequestAuthHeaders } from "@/lib/requestAuth";
import { getAIPrompt } from "@/lib/subjectPrompts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type CSVRow = {
  topic: string;
  question: string;
  option1: string;
  option2: string;
  option3: string;
  option4: string;
  correctAnswer: number;
  explanation: string;
  usesDesmos?: boolean;
  graphExpression?: string;
  imageUrl?: string;
  difficulty: DifficultyLevel;
  mode?: QuestionMode;
  answer?: string;
  category?: string;
  yearLevel?: string;
};

export default function AdminDashboard() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const questionCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const { profile, loading: profileLoading } = useProfile();
  const { effectiveRole } = useRolePerspective(profile?.id, profile?.role);
  const { currentSubject, subjects, isLoading: subjectsLoading } = useSubject();

  // Check if running on localhost for dev bypass
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const isAdminOrLocalhost = effectiveRole === 'admin' || isLocalhost;

  // New Tab State
  const [activeMainTab, setActiveMainTab] = useState<
    "quiz" | "drill" | "teacher"
  >("quiz");
  const [activeTeacherMode, setActiveTeacherMode] = useState<
    "worksheet" | "starter"
  >("worksheet");
  const activeMode: QuestionMode =
    activeMainTab === "teacher"
      ? activeTeacherMode
      : (activeMainTab as QuestionMode);

  const getActiveModeDetails = () => {
    switch (activeMode) {
      case "drill":
        return {
          label: "Practice Drills",
          template: "Drill_Template",
        };
      case "worksheet":
        return {
          label: "Worksheet Generator",
          template: "Worksheet_Template",
        };
      case "starter":
        return { label: "Starters", template: "Starter_Template" };
      case "quiz":
      default:
        return { label: "Quiz Mode", template: "Quiz_Template" };
    }
  };
  const modeDetails = getActiveModeDetails();

  // CSV Upload state
  const [csvPreviewData, setCSVPreviewData] = useState<CSVRow[]>([]);
  const [csvError, setCSVError] = useState<string | null>(null);
  const [csvFile, setCSVFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [newTopicYearLevel, setNewTopicYearLevel] = useState<string>("");
  const [yearOptions, setYearOptions] = useState<YearLevelSummary[]>([]);
  const [isAddingCustomYear, setIsAddingCustomYear] = useState(false);
  const [newCustomYearValue, setNewCustomYearValue] = useState("");
  const [newTopicCategory, setNewTopicCategory] = useState("");
  const [categoryOptions, setCategoryOptions] = useState<TopicCategoryOption[]>([]);
  const [sessionCategories, setSessionCategories] = useState<string[]>([]);
  const [isAddingCustomCategory, setIsAddingCustomCategory] = useState(false);
  const [newCustomCategoryValue, setNewCustomCategoryValue] = useState("");
  const [csvUploadTargetMode, setCsvUploadTargetMode] = useState<string>("new");
  const [csvUploadTargetTopicId, setCsvUploadTargetTopicId] = useState<string>("");

  const [newTopicSubject, setNewTopicSubject] = useState(currentSubject?.id || "");
  const [isAddingCustomSubject, setIsAddingCustomSubject] = useState(false);
  const [newCustomSubjectValue, setNewCustomSubjectValue] = useState("");

  // Question form state
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [isEditingTopicName, setIsEditingTopicName] = useState(false);
  const [editingTopicNameValue, setEditingTopicNameValue] = useState("");
  const [editingTopicYearLevelValue, setEditingTopicYearLevelValue] = useState("");
  const [editingTopicCategoryValue, setEditingTopicCategoryValue] = useState("");
  const [isEditingTopicAddingCustomYear, setIsEditingTopicAddingCustomYear] = useState(false);
  const [editingTopicCustomYearValue, setEditingTopicCustomYearValue] = useState("");
  const [isEditingTopicAddingCustomCategory, setIsEditingTopicAddingCustomCategory] = useState(false);
  const [editingTopicCustomCategoryValue, setEditingTopicCustomCategoryValue] = useState("");
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [questionForm, setQuestionForm] = useState<Partial<Question>>({
    question: "",
    options: ["", "", "", ""],
    correctAnswer: 0,
    explanation: "",
    usesDesmos: false,
    difficulty: 6,
    mode: "quiz",
  });

  // Module Status state
  const [moduleStatuses, setModuleStatuses] = useState<import('@shared/schema').ModuleStatus[]>([]);
  const [isUpdatingModuleStatus, setIsUpdatingModuleStatus] = useState<string | null>(null);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        if (!currentSubject) return;
        setLoading(true);
        const authHeaders = await getRequestAuthHeaders();

        const isJsonResponse = (res: Response) =>
          res.ok && (res.headers.get('content-type') || '').includes('application/json');

        const [topicsResponse, categoriesResponse, yearLevelsResponse, statusResponse] = await Promise.all([
          fetch(`/api/topics?subject=${currentSubject.id}`, { headers: authHeaders }),
          fetch("/api/topic-categories"),
          fetch(`/api/year-levels?subject=${currentSubject.id}`, { headers: authHeaders }),
          fetch(`/api/module-status?subject=${currentSubject.id}&_t=${Date.now()}`, { headers: authHeaders }),
        ]);

        if (isJsonResponse(topicsResponse)) {
          const data = await topicsResponse.json();
          setTopics(filterTopicsBySubject(data as Topic[], currentSubject.id));
        }

        if (isJsonResponse(categoriesResponse)) {
          const data = await categoriesResponse.json();
          setCategoryOptions(data);
        }

        if (isJsonResponse(yearLevelsResponse)) {
          const data = await yearLevelsResponse.json();
          setYearOptions(data);
        }

        if (isJsonResponse(statusResponse)) {
          const data = await statusResponse.json();
          setModuleStatuses(data);
        }
      } catch (error) {
        console.error("Failed to load admin metadata:", error);
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
  }, [currentSubject]);

  useEffect(() => {
    if (currentSubject) {
      setNewTopicSubject(currentSubject.id);
      setSelectedTopic(null);
    }
  }, [currentSubject?.id]);

  useEffect(() => {
    if (!newTopicYearLevel && yearOptions.length > 0) {
      setNewTopicYearLevel(yearOptions[0].year);
    }
  }, [newTopicYearLevel, yearOptions]);

  useEffect(() => {
    if (!newTopicCategory && categoryOptions.length > 0) {
      setNewTopicCategory(categoryOptions[0].name);
    }
  }, [newTopicCategory, categoryOptions]);

  useEffect(() => {
    if (!profileLoading && !isAdminOrLocalhost) {
      setLocation("/");
    }
  }, [isAdminOrLocalhost, profileLoading, setLocation]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setLocation("/");
  };

  const fetchModuleStatuses = async () => {
    if (!currentSubject) return;
    try {
      const authHeaders = await getRequestAuthHeaders();
      const res = await fetch(`/api/module-status?subject=${currentSubject.id}&_t=${Date.now()}`, { headers: authHeaders });
      const contentType = res.headers.get('content-type') || '';
      if (res.ok && contentType.includes('application/json')) {
        const data = await res.json();
        setModuleStatuses(data);
      }
    } catch (err) {
      console.error('Failed to re-fetch module statuses:', err);
    }
  };

  const handleUpdateModuleStatus = async (moduleName: string, status: 'active' | 'coming_soon' | 'maintenance' | 'hidden', applyToAll = false) => {
    setIsUpdatingModuleStatus(moduleName);
    try {
      const authHeaders = await getRequestAuthHeaders();
      const res = await fetch("/api/module-status", {
        method: "PUT",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ moduleName, status, subjectId: currentSubject?.id, applyToAll })
      });

      const contentType = res.headers.get('content-type') || '';
      if (!res.ok || !contentType.includes('application/json')) {
        const errText = contentType.includes('application/json')
          ? (await res.json()).error
          : `Server returned ${res.status} (not JSON)`;
        throw new Error(errText || "Failed to update status");
      }

      // Always re-fetch from DB to ensure we have the actual saved state
      await fetchModuleStatuses();
      toast({ title: "Success", description: `${moduleName} status updated to ${status}${applyToAll ? ' for all subjects' : ''}` });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsUpdatingModuleStatus(null);
    }
  };

  // Re-fetch topics from the API to ensure UI matches Supabase exactly
  const reloadTopics = async () => {
    try {
      const authHeaders = await getRequestAuthHeaders();
      const response = await fetch(`/api/topics?subject=${currentSubject.id}`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        const filtered = filterTopicsBySubject(data as Topic[], currentSubject.id);
        setTopics(filtered);
        if (selectedTopic) {
          const fresh = filtered.find((t: Topic) => t.id === selectedTopic.id);
          setSelectedTopic(fresh || null);
        }
      }
    } catch (error) {
      console.error("Failed to reload topics:", error);
    }
  };

  // Keep the working set scoped to the active subject before applying mode filters.
  const subjectTopics = currentSubject ? filterTopicsBySubject(topics, currentSubject.id) : [];
  const subjectCategoryNames = new Set(
    [...subjectTopics.map((topic) => topic.category), ...sessionCategories].filter(Boolean) as string[]
  );
  const visibleCategoryOptions = categoryOptions.filter((category) => subjectCategoryNames.has(category.name));

  // Filter topics by active mode
  const filteredTopics = subjectTopics.filter((t) => (t.mode || 'quiz') === activeMode);

  // CSV Upload Handlers
  const handleCSVFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCSVFile(file);
    setCSVError(null);

    // Parse and preview CSV
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: Papa.ParseResult<any>) => {
          if (!results.data || results.data.length === 0) {
            setCSVError("No data could be parsed from the CSV file.");
            return;
          }
          
          let parsedErrors = "";
          if (results.errors.length > 0) {
            parsedErrors = `Note: PapaParse reported ${results.errors.length} formatting errors (e.g. TooManyFields). These rows might be skipped or truncated. `;
          }

        const validQuestions: CSVRow[] = [];
        const errors: string[] = [];
        const seenQuestions = new Set<string>();

        results.data.forEach((row: any, index: number) => {
          // Normalize keys to lowercase for flexible matching
          const normalizedRow: any = {};
          Object.keys(row).forEach((key) => {
            normalizedRow[key.toLowerCase().trim()] = row[key];
          });

          // Check required fields
          const isStarter = activeMode === "starter" || activeMode === "worksheet";
          if (isStarter) {
            if ((!normalizedRow.topic && !normalizedRow.category) || (!normalizedRow.question && !normalizedRow.answer)) {
              errors.push(`Row ${index + 1}: Missing required fields for worksheet/starter (Topic, Question, or Answer)`);
              return;
            }
          } else {
            if ((!normalizedRow.topic && !normalizedRow.category) || !normalizedRow.question || (!normalizedRow.option1 && !normalizedRow.answer)) {
              errors.push(
                `Row ${index + 1}: Missing required fields (Topic, Question, and either Option1 or Answer)`
              );
              return;
            }
          }

          // Check for duplicates in this batch
          const uniqueKey = `${normalizedRow.topic || normalizedRow.category}-${normalizedRow.question}`.toLowerCase();
          if (seenQuestions.has(uniqueKey)) {
            errors.push(`Row ${index + 1}: Duplicate question ignored`);
            return;
          }
          seenQuestions.add(uniqueKey);

          // Validate difficulty
          let difficulty = Number(normalizedRow.difficulty);
          if (isNaN(difficulty) || difficulty < 4 || difficulty > 9) {
            difficulty = 5; // Default to 3 if invalid
            errors.push(
              `Row ${index + 1}: Invalid difficulty '${normalizedRow.difficulty}', defaulting to 5`,
            );
          }

          validQuestions.push({
            category: normalizedRow.category?.trim() || normalizedRow.topic?.trim(),
            yearLevel: normalizedRow['year level']?.trim() || normalizedRow.yearlevel?.trim() || normalizedRow.year?.trim(),
            topic: normalizedRow['sub topic']?.trim() || normalizedRow.subtopic?.trim() || (normalizedRow.category ? normalizedRow.topic?.trim() : ""),
            question: normalizedRow.question?.trim() || "",
            option1: normalizedRow.option1?.trim() || normalizedRow.answer?.trim() || "See answer/explain",
            option2: normalizedRow.option2?.trim() || "",
            option3: normalizedRow.option3?.trim() || "",
            option4: normalizedRow.option4?.trim() || "",
            correctAnswer: 0,
            explanation:
              normalizedRow.explanation?.trim() || "Uploaded by admin",
            usesDesmos:
              String(normalizedRow.usesdesmos).toLowerCase().trim() ===
                "true" ||
              String(normalizedRow.usesdesmos).trim() === "1" ||
              String(normalizedRow.usesdesmos).toLowerCase().trim() === "yes",
            graphExpression: normalizedRow['graph expression']?.trim() || normalizedRow.graphexpression?.trim() || normalizedRow['graph_expression']?.trim() || "",
            imageUrl: normalizedRow['image url']?.trim() || normalizedRow.imageurl?.trim() || normalizedRow['image_url']?.trim() || "",
            difficulty: difficulty as DifficultyLevel,
          });
        });

        if (validQuestions.length === 0) {
          setCSVError(
              parsedErrors + "No valid questions found in CSV. Please check the column headers.",
            );
            return;
          }
  
          if (errors.length > 0 || parsedErrors) {
            setCSVError(
              parsedErrors + `Found ${validQuestions.length} valid questions. ${errors.length} additional issues noted (e.g. duplicates or missing fields).`,
            );
          } else {
             setCSVError(null);
          }

          setCSVPreviewData(validQuestions);
        },
      error: () => setCSVError("Error reading CSV file"),
    });
  };

  
  const [mergeSourceTopicId, setMergeSourceTopicId] = useState("");
  const [mergeTargetTopicId, setMergeTargetTopicId] = useState("");
  const [isMerging, setIsMerging] = useState(false);
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);

  const handleMergeTopics = async () => {
    if (!mergeSourceTopicId || !mergeTargetTopicId) {
      toast({ title: "Error", description: "Please select both a source and target Sub Topic.", variant: "destructive" });
      return;
    }
    if (mergeSourceTopicId === mergeTargetTopicId) {
      toast({ title: "Error", description: "Source and target Sub Topics cannot be the same.", variant: "destructive" });
      return;
    }

    const sourceTopic = topics.find(t => t.id.toString() === mergeSourceTopicId);
    const targetTopic = topics.find(t => t.id.toString() === mergeTargetTopicId);

    if (!sourceTopic || !targetTopic) return;
    
    setIsMerging(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || null;
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // 1. Delete the source topic first (removes its questions from DB)
      // This prevents unique constraint violations when re-inserting source questions under the target
      const delRes = await fetch(`/api/topics/${String(sourceTopic.id)}?mode=${sourceTopic.mode || 'quiz'}`, {
        method: "DELETE",
        headers
      });

      if (!delRes.ok) {
        const errData = await delRes.json().catch(() => null);
        throw new Error(errData?.error || `Failed to delete source Sub Topic (${delRes.status})`);
      }

      // 2. Update target topic with combined questions
      const mergedTopic = {
        ...targetTopic,
        id: String(targetTopic.id),
        questions: [...targetTopic.questions, ...sourceTopic.questions]
      };

      const putRes = await fetch(`/api/topics/${String(targetTopic.id)}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(mergedTopic)
      });

      if (!putRes.ok) {
        const errData = await putRes.json().catch(() => null);
        const msg = errData?.details || errData?.error || `Failed to update target Sub Topic (${putRes.status})`;
        throw new Error(msg);
      }

      toast({ title: "Success", description: "Sub Topics merged successfully!" });
      setMergeSourceTopicId("");
      setMergeTargetTopicId("");
      setIsMergeDialogOpen(false);
      
      // Refresh topics from API
      await reloadTopics();
    } catch (error: any) {
      console.error(error);
      toast({ title: "Merge Failed", description: error.message, variant: "destructive" });
    } finally {
      setIsMerging(false);
    }
  };

  const handleSaveCSVQuestions = async () => {
    if (csvPreviewData.length === 0 || isUploading) return;
    setIsUploading(true);

    // Group questions by topic
    const questionsByTopic = csvPreviewData.reduce(
      (acc, row, idx) => {
        let topicName = row.topic;

        if (csvUploadTargetMode === "existing" && csvUploadTargetTopicId) {
          const existing = topics.find(t => t.id.toString() === csvUploadTargetTopicId);
          if (existing) topicName = existing.name;
        }
        const groupKey = `${topicName}___${row.mode || activeMode}`;
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push({

          id: `uploaded-${Date.now()}-${idx}`,
          question: row.question || "Missing question text",
          options: ((row.mode || activeMode) === "starter" || (row.mode || activeMode) === "worksheet") ? [row.answer || row.option1 || "", ""] : [(row.option1 || ""), (row.option2 || ""), (row.option3 || ""), (row.option4 || "")],
          correctAnswer: ((row.mode || activeMode) === "starter" || (row.mode || activeMode) === "worksheet") ? 0 : (Number(row.correctAnswer) || 0),
          explanation: row.explanation || "",
          usesDesmos: String(row.usesDesmos).toLowerCase() === "true",
          graphExpression: row.graphExpression || "",
          imageUrl: row.imageUrl || "",
          difficulty: (Number(row.difficulty) || 6) as any,
          mode: activeMode,
          yearLevel: row.yearLevel || newTopicYearLevel,
          category: row.category || newTopicCategory || "Uncategorized",
        });
        return acc;
      },
      {} as Record<string, Question[]>,
    );

    let totalQuestionsAdded = 0;
    let topicsCreated = 0;
    let topicsUpdated = 0;

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || null;

      // Process each topic
      for (const [topicKey, questions] of Object.entries(questionsByTopic)) {
        const topicName = topicKey.split("___")[0];
        let existingTopic: Topic | undefined;
        
        if (csvUploadTargetMode === "existing" && csvUploadTargetTopicId) {
          existingTopic = topics.find(t => t.id.toString() === csvUploadTargetTopicId);
        } else {
          existingTopic = undefined;
        }

        let topicToSave: Topic;

        if (existingTopic) {
          topicToSave = {
            ...existingTopic,
            is_core: true, // Admin uploads always promote to core
            questions: [...existingTopic.questions, ...questions],
          };
          topicsUpdated++;
        } else {
          const defaultYearLevel = questions[0]?.yearLevel || newTopicYearLevel;
          const safeTopicName = topicName || "Uncategorized Topic";
          // Use the subject label (e.g. "Physics"), not the UUID, so DB stores correctly
          const resolvedSubject = currentSubject?.label || newTopicSubject;
          topicToSave = {
            id: `core-${Date.now()}-${safeTopicName.replace(/\s+/g, "-").toLowerCase()}`,
            name: safeTopicName,
            icon: "",
            yearLevel: defaultYearLevel ? String(defaultYearLevel) : "10",
            category: questions[0]?.category || newTopicCategory || "Uncategorized",
            subject: resolvedSubject,
            mode: activeMode,
            questions: questions,
            is_core: true,
          };
          topicsCreated++;
        }

        // Save to API only — do NOT update local state
        const response = await fetch("/api/topics", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(topicToSave),
        });

        if (response.ok) {
          totalQuestionsAdded += questions.length;
        } else {
          const errData = await response.json().catch(() => null);
          const errMsg = errData?.details || errData?.error || `Failed to save topic: ${topicName} (Status: ${response.status})`;
          throw new Error(`Error (${response.status}): ${errMsg}`);
        }
      }

      // Re-fetch topics from the API so UI matches Supabase exactly
      await reloadTopics();

      // Reset form
      setCSVFile(null);
      setCSVPreviewData([]);
      setCSVError(null);

      alert(
        `Successfully added ${totalQuestionsAdded} questions!\n` +
          `Sub Topics created: ${topicsCreated}\n` +
          `Sub Topics updated: ${topicsUpdated}`,
      );
    } catch (error: any) {
      console.error("Error saving topics:", error);
      setCSVError(error.message || "Error saving questions to database");
    } finally {
      setIsUploading(false);
    }
  };

  // Question CRUD Handlers
  const handleSaveQuestion = async () => {
    if (!selectedTopic || !questionForm.question) return;

    const newQuestion: Question = {
      id: editingQuestion?.id || `q${Date.now()}`,
      question: questionForm.question,
      options: (activeMode === "starter" || activeMode === "worksheet") ? [questionForm.answer || "", ""] : (questionForm.options || ["", "", "", ""]),
      correctAnswer: (activeMode === "starter" || activeMode === "worksheet") ? 0 : (questionForm.correctAnswer || 0),
      explanation: questionForm.explanation || "",
      usesDesmos: questionForm.usesDesmos || false,
      difficulty: (questionForm.difficulty || 6) as DifficultyLevel,
      mode: activeMode,
    };

    const updatedTopic = {
      ...selectedTopic,
      questions: editingQuestion
        ? selectedTopic.questions.map((q) =>
            q.id === editingQuestion.id ? newQuestion : q,
          )
        : [...selectedTopic.questions, newQuestion],
    };

    // Save to API first, then reload from server
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || null;

      const response = await fetch("/api/topics", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(updatedTopic),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.error || `Save failed (${response.status})`);
      }

      await reloadTopics();
    } catch (error: any) {
      console.error("Error saving question:", error);
      alert(`Failed to save question: ${error.message}`);
    }

    resetQuestionForm();
  };

  const handleDeleteQuestion = async (topicId: string, questionId: string) => {
    if (!confirm("Are you sure you want to delete this question?")) return;

    const topicToUpdate = topics.find((t) => t.id === topicId);
    if (!topicToUpdate) return;

    const updatedTopic = {
      ...topicToUpdate,
      questions: topicToUpdate.questions.filter((q) => q.id !== questionId),
    };

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || null;

      const response = await fetch("/api/topics", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(updatedTopic),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.error || `Delete failed (${response.status})`);
      }

      await reloadTopics();
    } catch (error: any) {
      console.error("Error deleting question:", error);
      alert(`Failed to delete question: ${error.message}`);
    }
  };

  const handleDeleteTopic = async (topicId: string, mode: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this topic and ALL of its questions?",
      )
    )
      return;

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || null;
      const response = await fetch(`/api/topics/${topicId}?mode=${mode}`, {
        method: "DELETE",
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.error || `Delete failed (${response.status})`);
      }

      if (selectedTopic && selectedTopic.id === topicId) {
        setSelectedTopic(null);
      }
      await reloadTopics();
    } catch (error: any) {
      console.error("Error deleting topic:", error);
      alert(`Failed to delete topic: ${error.message}`);
    }
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setIsAddingQuestion(false);
    
    // For single-answer modes, extract the answer from the first option if it exists
    const isTeacherMode = activeMode === "starter" || activeMode === "worksheet";
    const answer = isTeacherMode && question.options && question.options.length > 0 
      ? question.options[0] 
      : question.answer || "";
      
    setQuestionForm({ ...question, answer });
  };

  const handleDuplicateQuestion = (question: Question) => {
    setEditingQuestion(null);
    setIsAddingQuestion(true);
    
    // For single-answer modes, extract the answer from the first option if it exists
    const isTeacherMode = activeMode === "starter" || activeMode === "worksheet";
    const answer = isTeacherMode && question.options && question.options.length > 0 
      ? question.options[0] 
      : question.answer || "";
      
    setQuestionForm({ ...question, answer });
    
    // Scroll to the top of the form area
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!editingQuestion?.id) return;
    const editorCard = questionCardRefs.current[String(editingQuestion.id)];
    if (editorCard) {
      editorCard.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [editingQuestion?.id]);

  const resetQuestionForm = () => {
    setEditingQuestion(null);
    setIsAddingQuestion(false);
    setQuestionForm({
      question: "",
      options: ["", "", "", ""],
      correctAnswer: 0,
      answer: "",
      explanation: "",
      usesDesmos: false,
      difficulty: 6,
      mode: activeMode,
    });
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...(questionForm.options || ["", "", "", ""])];
    newOptions[index] = value;
    setQuestionForm({ ...questionForm, options: newOptions });
  };

  const renderQuestionEditorFields = () => (
    <>
      <div>
        <Label>Question</Label>
        <Textarea
          value={questionForm.question}
          onChange={(e) =>
            setQuestionForm({
              ...questionForm,
              question: e.target.value,
            })
          }
          placeholder="Enter the question... (Use $$ for display math and $ for inline math)"
          rows={3}
          className="mt-2"
        />
        {questionForm.question && (
          <div className="mt-2 p-3 bg-muted/30 border rounded-md text-sm">
            <p className="text-xs font-semibold text-muted-foreground mb-1">Live Preview</p>
            <MathText>{String(questionForm.question || "")}</MathText>
          </div>
        )}
      </div>

      <div>
        <Label>
          Difficulty Level (4 = Easy, 9 = Grade 9 GCSE)
        </Label>
        <Select
          value={String(questionForm.difficulty || 6)}
          onValueChange={(v) =>
            setQuestionForm({
              ...questionForm,
              difficulty: Number(v) as DifficultyLevel,
            })
          }
        >
          <SelectTrigger className="mt-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Level 1</SelectItem>
            <SelectItem value="2">Level 2</SelectItem>
            <SelectItem value="3">Level 3</SelectItem>
            <SelectItem value="4">Level 4 - Easy</SelectItem>
            <SelectItem value="5">Level 5 - Basic</SelectItem>
            <SelectItem value="6">Level 6 - Foundation GCSE</SelectItem>
            <SelectItem value="7">Level 7 - Mid GCSE</SelectItem>
            <SelectItem value="8">Level 8 - Advanced GCSE</SelectItem>
            <SelectItem value="9">Level 9 - Grade 9 GCSE</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(activeMode === "starter" || activeMode === "worksheet") ? (
        <div>
          <Label>Answer (for Teachers)</Label>
          <Input
            value={questionForm.answer || ""}
            onChange={(e) => setQuestionForm({ ...questionForm, answer: e.target.value })}
            placeholder="Correct answer"
            className="mt-2"
          />
        </div>
      ) : (
        <div>
          <Label>Options</Label>
          <div className="space-y-4 mt-2">
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className="space-y-2 border rounded-md p-3 relative bg-card">
                <div className="flex gap-2 items-center">
                  <div className="flex-1">
                    <Input
                      value={questionForm.options?.[index] || ""}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`correct-${index}`}
                      checked={questionForm.correctAnswer === index}
                      onCheckedChange={() => setQuestionForm({ ...questionForm, correctAnswer: index })}
                    />
                    <Label htmlFor={`correct-${index}`} className="cursor-pointer">Correct</Label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <Label>Explanation</Label>
        <Textarea
          value={questionForm.explanation}
          onChange={(e) =>
            setQuestionForm({
              ...questionForm,
              explanation: e.target.value,
            })
          }
          placeholder="Explain the answer... (Use $$ for display math and $ for inline math)"
          rows={2}
          className="mt-2"
        />
        {questionForm.explanation && (
          <div className="mt-2 p-3 bg-muted/30 border rounded-md text-sm">
            <p className="text-xs font-semibold text-muted-foreground mb-1">Live Preview</p>
            <MathText>{String(questionForm.explanation || "")}</MathText>
          </div>
        )}
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          variant="outline"
          onClick={resetQuestionForm}
        >
          Cancel
        </Button>
        <Button onClick={handleSaveQuestion}>
          <Save className="h-4 w-4 mr-2" />
          Save Question
        </Button>
      </div>
    </>
  );

  // Show loading spinner while profile or topics are still loading on initial load.
  // Once admin is confirmed, don't blank the screen during auth re-fetches.
  const isInitialLoad = profileLoading && !profile;

  if (isInitialLoad || loading || subjectsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Only block access if profile has definitively loaded and user is NOT admin (or on localhost)
  if (!profileLoading && !isAdminOrLocalhost) {
    return null;
  }
  
  if (!currentSubject && subjects.length > 0) {
    return null; // Wait for subject to load
  }

  if (!currentSubject && subjects.length === 0) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <ShieldCheck className="h-16 w-16 text-primary mb-4" />
        <h1 className="text-3xl font-bold font-serif mb-2 text-center">Welcome to Admin Dashboard</h1>
        <p className="text-muted-foreground mb-8 text-center max-w-md">
          To get started, you need to create your first subject.
        </p>
        <Card className="p-6 w-full max-w-md">
          <h2 className="text-xl font-semibold mb-4">Create First Subject</h2>
          <div className="space-y-4">
            <div>
              <Label>Subject Name</Label>
              <Input 
                placeholder="e.g. Mathematics" 
                value={newCustomSubjectValue}
                onChange={(e) => setNewCustomSubjectValue(e.target.value)}
                className="mt-2"
              />
            </div>
            <Button 
              className="w-full"
              onClick={async () => {
                const trimmedSubject = newCustomSubjectValue.trim();
                if (trimmedSubject) {
                  try {
                    const slug = trimmedSubject.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                    const theme = `theme-${slug}`;
                    const authHeaders = await getRequestAuthHeaders();
                    const response = await fetch("/api/subjects", {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        ...authHeaders,
                      },
                      body: JSON.stringify({ label: trimmedSubject, slug, theme }),
                    });

                    if (!response.ok) {
                      throw new Error("Failed to add subject");
                    }

                    toast({ title: "Success", description: "Subject added successfully. Please refresh the page." });
                    window.location.reload();
                  } catch (error: any) {
                    console.error(error);
                    toast({ title: "Error", description: error.message || "Failed to add subject", variant: "destructive" });
                  }
                }
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Subject
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary/10 rounded-lg">
                <ShieldCheck className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold font-serif">
                  Admin Dashboard
                </h1>
                <p className="text-muted-foreground mt-1">
                  Manage questions by mode - {profile?.display_name || 'Admin'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setLocation("/")}>
                <BookOpen className="h-4 w-4 mr-2" />
                Back to App
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>

          {/* Module Access Settings */}
          <Card className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                Module Access Settings
              </h3>
              <p className="text-sm text-muted-foreground">Control which sections of the app are visible to students and teachers.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {['quiz', 'drill', 'statistics', 'review', 'worksheet', 'starter', 'my_question_banks'].map((moduleName) => {
                const displayNames: Record<string, string> = {
                  quiz: 'Quiz',
                  drill: 'Drill',
                  statistics: 'Statistics',
                  review: 'Review',
                  worksheet: 'Worksheet',
                  starter: 'Starter',
                  my_question_banks: 'My Question Banks'
                };
                const currentStatus = moduleStatuses.find(m => m.moduleName === moduleName)?.status || 'active';
                const isUpdating = isUpdatingModuleStatus === moduleName;
                return (
                  <div key={moduleName} className="space-y-2 border rounded-lg p-4">
                    <Label className="font-semibold text-lg">{displayNames[moduleName] || moduleName}</Label>
                    <Select 
                      disabled={isUpdating}
                      value={currentStatus} 
                      onValueChange={(val: any) => handleUpdateModuleStatus(moduleName, val)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="coming_soon">Coming Soon</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="hidden">Hidden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Mode Tabs */}
          <Card className="p-6">
            <Tabs
              value={activeMainTab}
              onValueChange={(v) => {
                setActiveMainTab(v as any);
                resetQuestionForm();
                setSelectedTopic(null);
              }}
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="quiz">
                  Quiz
                </TabsTrigger>
                <TabsTrigger value="drill">
                  Practice Drills
                </TabsTrigger>
                <TabsTrigger value="teacher">
                  Teacher Section
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {activeMainTab === "teacher" && (
              <div className="mt-6 border-b pb-4">
                <Tabs
                  value={activeTeacherMode}
                  onValueChange={(v) => {
                    setActiveTeacherMode(v as any);
                    resetQuestionForm();
                    setSelectedTopic(null);
                  }}
                >
                  <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
                    <TabsTrigger value="worksheet">
                      Worksheet Generator
                    </TabsTrigger>
                    <TabsTrigger value="starter">
                      Starters
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            )}

            <div className="space-y-6 mt-6">
              {/* CSV Upload Section */}
              <Card className="p-6 border-2 border-dashed">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold">
                      Bulk Upload CSV
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Upload questions in bulk for {modeDetails?.label}.
                      Duplicate questions will be skipped.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const isStarterOrWorksheet = activeMode === "starter" || activeMode === "worksheet";
                      const csvContent = isStarterOrWorksheet
                        ? "Topic,Sub Topic,Year Level,Question,Answer,Explanation,UsesDesmos,GraphExpression,ImageUrl,Difficulty\nAlgebra,Simplifying Expressions,7,Simplify $3x + 2x$,$5x$,$3x + 2x = 5x$ (combine like terms),FALSE,,,5"
                        : "Topic,Sub Topic,Year Level,Question,Option1,Option2,Option3,Option4,Explanation,UsesDesmos,GraphExpression,ImageUrl,Difficulty\nAlgebra,Solving Equations,7,Solve $2x = 10$,$x = 5$,$x = 10$,$x = 2$,$x = 20$,Divide both sides by 2: $x = 10 \\div 2 = 5$,FALSE,,,5";
                      const blob = new Blob([csvContent], { type: "text/csv" });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${modeDetails.template}.csv`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </Button>
                </div>
                {/* AI Generator Prompt Section */}
                  <div className="mb-6 border border-primary/20 rounded-lg p-4 bg-primary/5">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-sm flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-primary" />
                        AI Generator Prompt
                      </h3>
                      <Button
                        variant="default"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => {
                          const text = document.getElementById("ai-prompt-content")?.innerText;
                          if (text) {
                            navigator.clipboard.writeText(text).catch(err => console.error("Failed to copy:", err));
                            toast({ title: "Copied!", description: "Prompt copied to clipboard." });
                          }
                        }}>
                        Copy Prompt
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      Copy this prompt into ChatGPT or Claude to generate correctly formatted questions.
                    </p>
                    <div id="ai-prompt-content" className="bg-background rounded p-3 text-xs font-mono text-muted-foreground whitespace-pre-wrap border select-all h-40 overflow-y-auto">
                      {getAIPrompt(currentSubject?.label || '', activeMode, true)}
                    </div>
                  </div>
                  <div className="space-y-4 pt-2">
                  <div 
                    className="relative border-2 border-dashed border-primary/30 rounded-lg p-8 hover:bg-primary/5 transition-colors group cursor-pointer overflow-hidden"
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const file = e.dataTransfer.files?.[0];
                      if (file) {
                        handleCSVFileSelect({ target: { files: [file] } } as any);
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => {
                        handleCSVFileSelect(e as any);
                        e.target.value = '';
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 block"
                      style={{ height: '100%', width: '100%' }}
                    />
                    <div className="text-center pointer-events-none relative z-0">
                      <Upload className="h-8 w-8 text-primary/60 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                      <h4 className="font-semibold text-primary">
                        Click or drag CSV file here
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {activeMode === "starter" || activeMode === "worksheet" 
                          ? "Required columns: Topic, Sub Topic, Year Level, Question, Answer, Explanation, UsesDesmos, GraphExpression, ImageUrl, Difficulty (4-9)"
                          : "Required columns: Topic, Sub Topic, Year Level, Question, Option1 (correct), Option2, Option3, Option4, Explanation, UsesDesmos, GraphExpression, ImageUrl, Difficulty (4-9)"
                        }
                      </p>
                    </div>
                  </div>

                  {/* Validation Error/Warnings (Shown even if no valid questions) */}
                  {csvError && (
                    <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                      <p className="font-semibold">Warning: Issues found:</p>
                      <p>{csvError}</p>
                    </div>
                  )}

                  {csvPreviewData.length > 0 && (
                    <>
                      <div className="border rounded-lg p-4 bg-muted/20">
                        <h4 className="font-semibold mb-2">
                          Preview: {csvPreviewData.length} valid questions found
                        </h4>

                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {csvPreviewData.slice(0, 3).map((q, idx) => (
                            <div
                              key={idx}
                              className="text-sm bg-background p-2 rounded border"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="secondary">{q.topic}</Badge>
                                <Badge
                                  variant={
                                    q.difficulty >= 4 && q.difficulty <= 9
                                      ? "outline"
                                      : "destructive"
                                  }
                                >
                                  Lvl {q.difficulty}
                                </Badge>
                              </div>
                              <div className="font-medium truncate">
                                <MathDisplay>{q.question}</MathDisplay>
                              </div>
                            </div>
                          ))}
                          {csvPreviewData.length > 3 && (
                            <p className="text-xs text-muted-foreground text-center py-2">
                              + {csvPreviewData.length - 3} more questions
                            </p>
                          )}
                        </div>

                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm font-medium mb-1">
                            Sub Topics found:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {Array.from(
                              new Set(csvPreviewData.map((q) => q.topic)),
                            ).map((topic) => (
                              <Badge key={topic} variant="outline">
                                {topic}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Default settings for new topics */}
                      <div className="border rounded-lg p-4 bg-muted/10">
                        <h4 className="font-semibold mb-3 text-sm">
                          Default Settings for New Sub Topics
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label className="text-sm">Subject</Label>
                              <Select
                                value={newTopicSubject}
                                onValueChange={setNewTopicSubject}
                              >
                                <SelectTrigger className="mt-2 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {subjects.map((subject) => (
                                    <SelectItem key={subject.id} value={subject.id}>
                                      {subject.label}
                                    </SelectItem>
                                  ))}
                                  <div
                                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none hover:bg-accent hover:text-accent-foreground font-medium text-primary mt-1 border-t"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setIsAddingCustomSubject(true);
                                      setNewCustomSubjectValue("");
                                    }}
                                  >
                                    <Plus className="mr-2 h-4 w-4" />
                                    Add New Subject...
                                  </div>
                                </SelectContent>
                              </Select>
                            </div>
                            {isAddingCustomSubject && (
                              <div className="col-span-full mb-2 p-3 border rounded bg-background">
                                <Label className="text-sm">New Subject Name</Label>
                                <div className="flex items-center gap-2 mt-2">
                                  <Input
                                    value={newCustomSubjectValue}
                                    onChange={(e) => setNewCustomSubjectValue(e.target.value)}
                                    placeholder="e.g. Psychology"
                                    className="text-sm"
                                  />
                                  <Button
                                    size="sm"
                                    onClick={async () => {
                                      const trimmedSubject = newCustomSubjectValue.trim();
                                      if (trimmedSubject) {
                                        try {
                                          const slug = trimmedSubject.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                                          const theme = `theme-${slug}`;
                                          const { data: sessionData } = await supabase.auth.getSession();
                                          const token = sessionData.session?.access_token || null;
                                          const response = await fetch("/api/subjects", {
                                            method: "POST",
                                            headers: {
                                              "Content-Type": "application/json",
                                              ...(token ? { Authorization: `Bearer ${token}` } : {}),
                                            },
                                            body: JSON.stringify({ label: trimmedSubject, slug, theme }),
                                          });

                                          if (!response.ok) {
                                            throw new Error("Failed to add subject");
                                          }

                                          const createdSubject = await response.json();
                                          setNewTopicSubject(createdSubject.id);
                                          toast({ title: "Success", description: "Subject added successfully. Please refresh the page to see changes everywhere." });
                                        } catch (error: any) {
                                          console.error(error);
                                          toast({ title: "Error", description: error.message || "Failed to add subject", variant: "destructive" });
                                        }
                                      }
                                      setIsAddingCustomSubject(false);
                                      setNewCustomSubjectValue("");
                                    }}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setIsAddingCustomSubject(false);
                                      setNewCustomSubjectValue("");
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            )}
                            <div>
                              <Label className="text-sm">Year Level</Label>
                                {isAddingCustomYear ? (
                                  <div className="flex items-center gap-2 mt-2">
                                    <Input
                                      value={newCustomYearValue}
                                      onChange={(e) => setNewCustomYearValue(e.target.value)}
                                      placeholder="e.g. IBDP Year 1"
                                      className="text-sm"
                                    />
                                    <Button
                                      size="sm"
                                      onClick={async () => {
                                        const trimmedYear = newCustomYearValue.trim();
                                        if (trimmedYear) {
                                          try {
                                            const { data: sessionData } = await supabase.auth.getSession();
                                            const token = sessionData.session?.access_token || null;
                                            const response = await fetch("/api/year-levels", {
                                              method: "POST",
                                              headers: {
                                                "Content-Type": "application/json",
                                                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                                              },
                                              body: JSON.stringify({ value: trimmedYear }),
                                            });

                                            if (!response.ok) {
                                              throw new Error("Failed to add year level");
                                            }

                                            const createdYear = await response.json();
                                            setYearOptions((prev) => [...prev, createdYear].sort((a, b) => a.sortOrder - b.sortOrder));
                                            setNewTopicYearLevel(createdYear.year);
                                            toast({ title: "Success", description: "Year level added successfully." });
                                          } catch (error: any) {
                                            console.error(error);
                                            toast({ title: "Error", description: error.message || "Failed to add year level", variant: "destructive" });
                                          }
                                        }
                                        setIsAddingCustomYear(false);
                                        setNewCustomYearValue("");
                                        setCsvUploadTargetMode("new");
                                        setCsvUploadTargetTopicId("");
                                      }}
                                    >
                                      Add
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setIsAddingCustomYear(false)}>Cancel</Button>
                                  </div>
                                ) : (
                                  <Select
                                    value={newTopicYearLevel}
                                    onValueChange={(v) => {
                                      if (v === "add_new_year") {
                                        setIsAddingCustomYear(true);
                                      } else {
                                        setNewTopicYearLevel(v);
                                      }
                                      setCsvUploadTargetMode("new");
                                      setCsvUploadTargetTopicId("");
                                    }}
                                  >
                                    <SelectTrigger className="mt-2 text-sm">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {yearOptions.map((yearLevel) => (
                                        <SelectItem key={yearLevel.year} value={yearLevel.year}>
                                          {yearLevel.grade}
                                        </SelectItem>
                                      ))}
                                      <SelectItem value="add_new_year" className="text-primary italic">+ Add New Year...</SelectItem>
                                    </SelectContent>
                                  </Select>
                                )}

                            </div>
                            <div>
                              <Label className="text-sm">Sub Topic (Section)</Label>
                              {isAddingCustomCategory ? (
                                <div className="flex items-center gap-2 mt-2">
                                  <Input
                                    value={newCustomCategoryValue}
                                    onChange={(e) => setNewCustomCategoryValue(e.target.value)}
                                    placeholder="e.g. Functions"
                                    className="text-sm"
                                  />
                                  <Button
                                    size="sm"
                                    onClick={async () => {
                                      const trimmedCategory = newCustomCategoryValue.trim();
                                      if (trimmedCategory) {
                                        try {
                                          const { data: sessionData } = await supabase.auth.getSession();
                                          const token = sessionData.session?.access_token || null;
                                          const response = await fetch("/api/topic-categories", {
                                            method: "POST",
                                            headers: {
                                              "Content-Type": "application/json",
                                              ...(token ? { Authorization: `Bearer ${token}` } : {}),
                                            },
                                            body: JSON.stringify({ name: trimmedCategory }),
                                          });

                                          if (!response.ok) {
                                            throw new Error("Failed to add topic");
                                          }

                                          const createdCategory = await response.json();
                                          setCategoryOptions((prev) => [...prev, createdCategory].sort((a, b) => a.sortOrder - b.sortOrder));
                                          setSessionCategories((prev) => [...prev, createdCategory.name]);
                                          setNewTopicCategory(createdCategory.name);
                                          toast({ title: "Success", description: "Topic added successfully." });
                                        } catch (error: any) {
                                          console.error(error);
                                          toast({ title: "Error", description: error.message || "Failed to add topic", variant: "destructive" });
                                        }
                                      }
                                      setIsAddingCustomCategory(false);
                                      setNewCustomCategoryValue("");
                                      setCsvUploadTargetMode("new");
                                      setCsvUploadTargetTopicId("");
                                    }}
                                  >
                                    Add
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setIsAddingCustomCategory(false)}>Cancel</Button>
                                </div>
                              ) : (
                                <Select
                                  value={newTopicCategory}
                                  onValueChange={(v) => {
                                    if (v === "add_new_category") {
                                      setIsAddingCustomCategory(true);
                                    } else {
                                      setNewTopicCategory(v);
                                    }
                                    setCsvUploadTargetMode("new");
                                    setCsvUploadTargetTopicId("");
                                  }}
                                >
                                  <SelectTrigger className="mt-2 text-sm">
                                    <SelectValue placeholder="Select Topic..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {visibleCategoryOptions.map((category) => (
                                      <SelectItem key={category.id} value={category.name}>
                                        {category.name}
                                      </SelectItem>
                                    ))}
                                    <SelectItem value="add_new_category" className="text-primary italic">+ Add New Topic...</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 mt-4">
                            <div>
                              <Label className="text-sm">Import Target</Label>
                              <Select
                                value={csvUploadTargetMode}
                                onValueChange={(v) => {
                                  setCsvUploadTargetMode(v);
                                  if (v !== "existing") setCsvUploadTargetTopicId("");
                                }}
                              >
                                <SelectTrigger className="mt-2 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new">Use CSV Sub Topicnames</SelectItem>
                                  <SelectItem value="existing">Add to existing Sub Topic</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {csvUploadTargetMode === "existing" && (
                              <div>
                                <Label className="text-sm">Selected Sub Topic</Label>
                                <Select
                                  value={csvUploadTargetTopicId}
                                  onValueChange={setCsvUploadTargetTopicId}
                                >
                                  <SelectTrigger className="mt-2 text-sm">
                                    <SelectValue placeholder="Select Sub Topic..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {topics
                                      .filter((t) => (t.mode || "quiz") === activeMode)
                                      .map((t) => (
                                        <SelectItem key={t.id.toString()} value={t.id.toString()}>
                                          {t.name}
                                          {t.yearLevel ? ` • ${String(t.yearLevel).match(/^\d+$/) ? `Year ${t.yearLevel}` : String(t.yearLevel)}` : ""}
                                          {t.category ? ` • ${t.category}` : ""}
                                        </SelectItem>
                                      ))}
                                    {topics.filter((t) => (t.mode || "quiz") === activeMode).length === 0 && (
                                      <SelectItem value="none" disabled>No topics available</SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                      </div>

                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setCSVFile(null);
                            setCSVPreviewData([]);
                            setCSVError(null);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSaveCSVQuestions}
                          className="w-full sm:w-auto"
                          disabled={csvPreviewData.length === 0 || isUploading}
                        >
                          <Save className="h-4 w-4 mr-2" />
                          {isUploading ? "Saving..." : "Save All to Database"}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Card>
              {/* MERGE SECTION */}
              <Card className="p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold">Merge Sub Topics</h3>
                    <p className="text-sm text-muted-foreground">Combine two existing Sub Topics into one. The source topic will be deleted and all its questions will be moved to the target topic.</p>
                  </div>
                  <Dialog open={isMergeDialogOpen} onOpenChange={setIsMergeDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline"><GitMerge className="mr-2 h-4 w-4" /> Merge</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Merge Sub Topics</DialogTitle>
                        <DialogDescription>
                          Select the source Sub Topic you want to empty out, and the target Sub Topic it should merge into.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                          <Label>Source Sub Topic (will be DELETED)</Label>
                          <Select value={mergeSourceTopicId} onValueChange={setMergeSourceTopicId}>
                            <SelectTrigger><SelectValue placeholder="Select source..." /></SelectTrigger>
                            <SelectContent>
                              {filteredTopics.map(t => (
                                <SelectItem key={t.id} value={t.id.toString()}>
                                  {t.name} (Year {t.yearLevel} - {t.category || "Uncategorized"})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-center text-muted-foreground"><ArrowDown className="h-6 w-6" /></div>
                        <div className="space-y-2">
                          <Label>Target Sub Topic (will KEEP original name)</Label>
                          <Select value={mergeTargetTopicId} onValueChange={setMergeTargetTopicId}>
                            <SelectTrigger><SelectValue placeholder="Select target..." /></SelectTrigger>
                            <SelectContent>
                              {filteredTopics.map(t => (
                                <SelectItem key={t.id} value={t.id.toString()}>
                                  {t.name} (Year {t.yearLevel} - {t.category || "Uncategorized"})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setIsMergeDialogOpen(false)}>Cancel</Button>
                        <Button 
                          variant="destructive" 
                          onClick={handleMergeTopics} 
                          disabled={isMerging}
                        >
                          {isMerging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                          Confirm Merge
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Topics List */}
                <Card className="p-6 lg:col-span-1">
                  <h3 className="text-lg font-semibold mb-4">
                    {modeDetails?.label} Topics
                  </h3>
                  <div className="space-y-2">
                    {filteredTopics.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No topics yet. Upload CSV or add questions.
                        </p>
                      ) : (
                        Object.entries(
                          filteredTopics.reduce((acc, topic) => {
                            const cat = topic.category || "Uncategorized";
                            if (!acc[cat]) acc[cat] = [];
                            acc[cat].push(topic);
                            return acc;
                          }, {} as Record<string, typeof filteredTopics>)
                        ).sort(([a], [b]) => a.localeCompare(b)).map(([category, categoryTopics]) => (
                          <div key={category} className="mb-4 space-y-2">
                            <h4 className="text-sm font-semibold text-primary/80 uppercase tracking-wider sticky top-0 bg-background/95 backdrop-blur z-10 py-1">{category}</h4>
                            {categoryTopics?.map((topic) => (
                              <Card
                                key={topic.id}
                                className={`p-3 group relative cursor-pointer transition-all ${
                                  selectedTopic?.id === topic.id
                                    ? "border-2 border-primary bg-primary/5"
                                    : "hover:border-primary/50"
                                }`}
                                onClick={() => {
                                  setSelectedTopic(topic);
                                  resetQuestionForm();
                                }}
                              >
                                <div className="pr-8">
                                  <p className="font-medium">{topic.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Year {topic.yearLevel} - {topic.questions.length} questions
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm("Delete this entire topic and all its questions?")) {
                                      handleDeleteTopic(topic.id, topic.mode || activeMode);
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </Card>
                            ))}
                          </div>
                        ))
                      )}
                  </div>
                </Card>

                {/* Questions Management */}
                <Card className="p-6 lg:col-span-2">
                  {selectedTopic ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          {isEditingTopicName ? (
                            <div className="flex flex-col gap-2 mb-2">
                              <div className="flex items-center gap-2">
                                <Input
                                  value={editingTopicNameValue}
                                  onChange={(e) => setEditingTopicNameValue(e.target.value)}
                                  className="h-8 min-w-[200px]"
                                  placeholder="Sub TopicName"
                                />
                                {isEditingTopicAddingCustomYear ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      value={editingTopicCustomYearValue}
                                      onChange={(e) => setEditingTopicCustomYearValue(e.target.value)}
                                      placeholder="e.g. 10"
                                      className="h-8 w-[80px] text-sm"
                                    />
                                    <Button
                                      size="sm"
                                      onClick={async () => {
                                        const trimmedYear = editingTopicCustomYearValue.trim();
                                        if (trimmedYear) {
                                          try {
                                            const { data: sessionData } = await supabase.auth.getSession();
                                            const token = sessionData.session?.access_token || null;
                                            const response = await fetch("/api/year-levels", {
                                              method: "POST",
                                              headers: {
                                                "Content-Type": "application/json",
                                                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                                              },
                                              body: JSON.stringify({ value: trimmedYear }),
                                            });

                                            if (!response.ok) {
                                              throw new Error("Failed to add year level");
                                            }

                                            const createdYear = await response.json();
                                            setYearOptions((prev) => [...prev, createdYear].sort((a, b) => a.sortOrder - b.sortOrder));
                                            setEditingTopicYearLevelValue(createdYear.year);
                                            toast({ title: "Success", description: "Year level added successfully." });
                                          } catch (error: any) {
                                            console.error(error);
                                            toast({ title: "Error", description: error.message || "Failed to add year", variant: "destructive" });
                                          }
                                        }
                                        setIsEditingTopicAddingCustomYear(false);
                                        setEditingTopicCustomYearValue("");
                                      }}
                                    >
                                      Add
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setIsEditingTopicAddingCustomYear(false)}>Cancel</Button>
                                  </div>
                                ) : (
                                  <Select 
                                    value={editingTopicYearLevelValue || String(selectedTopic.yearLevel)} 
                                    onValueChange={(v) => {
                                      if (v === "add_new_year") {
                                        setIsEditingTopicAddingCustomYear(true);
                                      } else {
                                        setEditingTopicYearLevelValue(v);
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-8 w-[140px]">
                                      <SelectValue placeholder="Year Level" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {yearOptions.map((yearLevel) => (
                                        <SelectItem key={yearLevel.year} value={yearLevel.year}>
                                          {yearLevel.grade}
                                        </SelectItem>
                                      ))}
                                      <SelectItem value="add_new_year" className="text-primary italic">+ Add New Year...</SelectItem>
                                    </SelectContent>
                                  </Select>
                                )}

                                {isEditingTopicAddingCustomCategory ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      value={editingTopicCustomCategoryValue}
                                      onChange={(e) => setEditingTopicCustomCategoryValue(e.target.value)}
                                      placeholder="e.g. Functions"
                                      className="h-8 w-[120px] text-sm"
                                    />
                                    <Button
                                      size="sm"
                                      onClick={async () => {
                                        const trimmedCategory = editingTopicCustomCategoryValue.trim();
                                        if (trimmedCategory) {
                                          try {
                                            const { data: sessionData } = await supabase.auth.getSession();
                                            const token = sessionData.session?.access_token || null;
                                            const response = await fetch("/api/topic-categories", {
                                              method: "POST",
                                              headers: {
                                                "Content-Type": "application/json",
                                                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                                              },
                                              body: JSON.stringify({ name: trimmedCategory }),
                                            });

                                            if (!response.ok) {
                                              throw new Error("Failed to add topic");
                                            }

                                            const createdCategory = await response.json();
                                            setCategoryOptions((prev) => [...prev, createdCategory].sort((a, b) => a.sortOrder - b.sortOrder));
                                            setEditingTopicCategoryValue(createdCategory.name);
                                            toast({ title: "Success", description: "Topic added successfully." });
                                          } catch (error: any) {
                                            console.error(error);
                                            toast({ title: "Error", description: error.message || "Failed to add topic", variant: "destructive" });
                                          }
                                        }
                                        setIsEditingTopicAddingCustomCategory(false);
                                        setEditingTopicCustomCategoryValue("");
                                      }}
                                    >
                                      Add
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setIsEditingTopicAddingCustomCategory(false)}>Cancel</Button>
                                  </div>
                                ) : (
                                  <Select 
                                    value={editingTopicCategoryValue || "Uncategorized"} 
                                    onValueChange={(v) => {
                                      if (v === "add_new_category") {
                                        setIsEditingTopicAddingCustomCategory(true);
                                      } else {
                                        setEditingTopicCategoryValue(v);
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-8 w-[160px]">
                                      <SelectValue placeholder="Topic" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                                      {visibleCategoryOptions.map((category) => (
                                        <SelectItem key={category.id} value={category.name}>{category.name}</SelectItem>
                                      ))}
                                      <SelectItem value="add_new_category" className="text-primary italic">+ Add New Topic...</SelectItem>
                                    </SelectContent>
                                  </Select>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button size="sm" onClick={async () => {
                                  if (!selectedTopic || !editingTopicNameValue.trim()) {
                                    setIsEditingTopicName(false);
                                    return;
                                  }
                                  const updatedTopic = { 
                                    ...selectedTopic, 
                                    name: editingTopicNameValue.trim(),
                                    yearLevel: editingTopicYearLevelValue || String(selectedTopic.yearLevel),
                                    category: editingTopicCategoryValue === "Uncategorized" ? undefined : editingTopicCategoryValue
                                  };
                                  setTopics(prev => prev.map(t => t.id === selectedTopic.id ? updatedTopic : t));
                                  setSelectedTopic(updatedTopic);
                                  setIsEditingTopicName(false);
                                  try {
                                    const { data: sessionData } = await supabase.auth.getSession();
                                    const token = sessionData.session?.access_token || null;
                                    const res = await fetch(`/api/topics/${selectedTopic.id}`, {
                                      method: "PUT",
                                      headers: {
                                        "Content-Type": "application/json",
                                        Authorization: `Bearer ${token}`
                                      },
                                      body: JSON.stringify(updatedTopic)
                                    });
                                    if (!res.ok) throw new Error("Failed to update topic");
                                    toast({ title: "Success", description: "Topic updated successfully." });
                                  } catch (err: any) {
                                    console.error(err);
                                    toast({ title: "Error", description: err.message || "Failed to update topic", variant: "destructive" });
                                  }
                                }}>Save</Button>
                                <Button size="sm" variant="ghost" onClick={() => setIsEditingTopicName(false)}>Cancel</Button> 
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 group">
                              <h3 className="text-lg font-semibold">
                                {selectedTopic.name}
                              </h3>
                              {selectedTopic.yearLevel && (
                                <Badge variant="secondary" className="font-normal text-xs">{selectedTopic.yearLevel}</Badge>
                              )}
                              {selectedTopic.category && (
                                <Badge variant="outline" className="font-normal text-xs">{selectedTopic.category}</Badge>
                              )}
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity ml-1" 
                                onClick={() => {
                                  setEditingTopicNameValue(selectedTopic.name);
                                  setEditingTopicYearLevelValue(String(selectedTopic.yearLevel));
                                  setEditingTopicCategoryValue(selectedTopic.category || "Uncategorized");
                                  setIsEditingTopicAddingCustomYear(false);
                                  setIsEditingTopicAddingCustomCategory(false);
                                  setIsEditingTopicName(true);
                                }}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                              </Button>
                            </div>
                          )}
                          <p className="text-sm text-muted-foreground">
                            {selectedTopic.questions.length} questions
                          </p>
                        </div>
                        <Button
                          onClick={() => {
                            resetQuestionForm();
                            setIsAddingQuestion(true);
                          }}
                          disabled={isAddingQuestion && !editingQuestion}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Question
                        </Button>
                      </div>

                      {/* Add/Edit Question Form */}
                      {isAddingQuestion && !editingQuestion && (
                        <Card className="p-4 border-2 border-primary">
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold">
                                {editingQuestion
                                  ? "Edit Question"
                                  : "New Question"}
                              </h4>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={resetQuestionForm}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            {renderQuestionEditorFields()}
                          </div>
                        </Card>
                      )}

                      {/* Questions List */}
                      <div className="space-y-3">
                        {selectedTopic.questions?.map((question, index) => {
                          const isEditingThisQuestion = String(editingQuestion?.id) === String(question.id);

                          return (
                          <Card
                            key={question.id}
                            ref={(el) => {
                              questionCardRefs.current[String(question.id)] = el;
                            }}
                            className={`p-4 ${isEditingThisQuestion ? "border-2 border-primary bg-primary/5" : ""}`}
                          >
                            <div className="space-y-3">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Badge variant="secondary">
                                      Q{index + 1}
                                    </Badge>
                                    <Badge variant="outline">
                                      Level {question.difficulty}
                                    </Badge>
                                  </div>
                                  <div className="font-medium">
                                    <MathDisplay>
                                      {question.question}
                                    </MathDisplay>
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleEditQuestion(question)}
                                    title="Edit Question"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDuplicateQuestion(question)}
                                    title="Duplicate Question"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                      handleDeleteQuestion(
                                        selectedTopic.id,
                                        question.id,
                                      )
                                    }
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </div>

                              {isEditingThisQuestion ? (
                                <div className="pt-3 border-t border-primary/20 space-y-4">
                                  <div className="flex items-center justify-between">
                                    <h4 className="font-semibold">Edit Question</h4>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={resetQuestionForm}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  {renderQuestionEditorFields()}
                                </div>
                              ) : (
                                <>
                                  <div className="space-y-1 text-sm">
                                    {question.options?.map((option, optIndex) => (
                                      <div
                                        key={optIndex}
                                        className={`p-2 rounded ${
                                          question.correctAnswer === optIndex
                                            ? "bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800"
                                            : "bg-muted/30"
                                        }`}
                                      >
                                        <MathDisplay>{option}</MathDisplay>
                                        {question.correctAnswer === optIndex && (
                                          <Badge
                                            variant="outline"
                                            className="ml-2 text-xs"
                                          >
                                            Correct
                                          </Badge>
                                        )}
                                      </div>
                                    ))}
                                  </div>

                                  <div className="text-sm text-muted-foreground">
                                    <strong>Explanation:</strong>{" "}
                                    <MathDisplay>
                                      {question.explanation}
                                    </MathDisplay>
                                  </div>
                                </>
                              )}
                            </div>
                          </Card>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground mb-4">
                        Select a topic to manage its questions
                      </p>
                      <Button disabled>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Question (Select Sub TopicFirst)
                      </Button>
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
